import { useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import Seo from "../../components/Seo";

const NAV = [
  { to: "/admin", label: "Dashboard", id: "dashboard", end: true },
  { to: "/admin/applications", label: "Candidatures", id: "applications" },
  { to: "/admin/people", label: "Personnes", id: "people" },
  { to: "/admin/profiles", label: "Profils / Créateurs", id: "profiles" },
  { to: "/admin/partners", label: "Partenaires", id: "partners" },
  { to: "/admin/events", label: "Événements", id: "events" },
  { to: "/admin/challenges", label: "Challenges", id: "challenges" },
  { to: "/admin/challenge-entries", label: "Entrées challenge", id: "entries" },
  { to: "/admin/media", label: "Médias", id: "media" },
  { to: "/admin/consents", label: "Consentements", id: "consents" },
  { to: "/admin/collaborators", label: "Collaborateurs", id: "collaborators" },
  { to: "/admin/audit", label: "Audit", id: "audit" },
];

const linkClass = ({ isActive }) =>
  `block whitespace-nowrap text-sm px-3 py-2 rounded transition-colors ${
    isActive ? "bg-[#FF2E63]/15 text-[#FF2E63]" : "text-neutral-400 hover:text-white"
  }`;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const current =
    [...NAV].reverse().find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to))) || NAV[0];

  return (
    <div className="min-h-screen bg-[#030303] text-white" data-testid="admin-layout">
      <Seo title="Administration" description="Console VytaliBoots" path="/admin" noindex />

      {/* Mobile header */}
      <div className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-black/80 border-b border-white/10">
        <div className="flex items-center justify-between px-5 h-16">
          <Link to="/" className="vb-display text-base font-black" data-testid="admin-home-link-mobile">
            VYTALI<span className="text-[#FF2E63]">BOOTS</span>
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="admin-mobile-menu-toggle"
            aria-label="Menu administration"
            className="flex items-center gap-2 text-sm text-neutral-300"
          >
            <span className="max-w-[140px] truncate">{current.label}</span>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-white/10 bg-[#050505] px-5 py-4 space-y-1 max-h-[70vh] overflow-y-auto" data-testid="admin-mobile-menu">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`admin-mobile-nav-${n.id}`}
                className={linkClass}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-4 mt-3 border-t border-white/10 text-xs text-neutral-500">
              <div>{user?.email}</div>
              <div className="mt-1">{user?.security_role}</div>
              <button onClick={logout} data-testid="admin-mobile-logout" className="mt-3 text-neutral-400 hover:text-white transition-colors">
                Déconnexion
              </button>
            </div>
          </nav>
        )}
      </div>

      <div className="flex">
        <aside className="hidden lg:block lg:w-64 lg:min-h-screen border-r border-white/10 p-6">
          <Link to="/" className="vb-display text-lg font-black block mb-8" data-testid="admin-home-link">
            VYTALI<span className="text-[#FF2E63]">BOOTS</span>
          </Link>
          <nav className="flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.id}`} className={linkClass}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-8 pt-6 border-t border-white/10 text-xs text-neutral-500">
            <div data-testid="admin-current-user">{user?.email}</div>
            <div className="mt-1">{user?.security_role}</div>
            <button onClick={logout} data-testid="admin-logout" className="mt-4 text-neutral-400 hover:text-white transition-colors">
              Déconnexion
            </button>
          </div>
        </aside>
        <div className="flex-1 min-w-0 p-6 lg:p-10 overflow-x-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
